
export enum AppStatus {
  SUBMITTED = 'SUBMITTED',
  UNDER_REVIEW = 'UNDER_REVIEW',
  BACKGROUND_CHECK_PENDING = 'BACKGROUND_CHECK_PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  LEASE_SENT = 'LEASE_SENT',
  LEASE_SIGNED = 'LEASE_SIGNED',
  SCHEDULED = 'SCHEDULED',
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  ADMIN_ARRIVED = 'ADMIN_ARRIVED',
  INSTALLED = 'INSTALLED',
  ACTIVE = 'ACTIVE',
  TERMINATED = 'TERMINATED',
  LEGAL_THEFT = 'LEGAL_THEFT',
  ARCHIVED = 'ARCHIVED'
}

export enum UserRole {
  GUEST = 'GUEST',
  CUSTOMER = 'CUSTOMER',
  STAFF = 'STAFF',
  ADMIN = 'ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN'
}

// Account types for role-based dashboard access
export enum AccountType {
  APPLIANCE_RENTER = 'APPLIANCE_RENTER'
}

// ============================================
// SaaS Multi-Company Types
// ============================================

export type SubscriptionTier = 'free' | 'starter' | 'professional' | 'unlimited';
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trialing';

export interface Company {
  id: string;
  name: string;                           // "Nestly Suite Inc."
  slug: string;                           // "nestly-suite" (for subdomain)
  email: string;                          // Primary contact email
  phone?: string;
  address?: string;

  // White-Label Customization
  logoUrl?: string;
  primaryColor?: string;                  // Hex color e.g., #0F172A
  accentColor?: string;                   // Hex color e.g., #2563EB
  customDomain?: string;                  // e.g., rental.mybrand.com
  supportPhone?: string;
  customTermsUrl?: string;
  customPrivacyUrl?: string;
  whiteLabelEmailHeader?: string;
  customCss?: string;

  // Owner
  ownerId: string;                        // Firebase Auth UID of owner
  ownerEmail: string;

  // Subscription & Billing
  subscriptionTier: SubscriptionTier;
  subscriptionStatus: SubscriptionStatus;
  customerLimit: number;                  // Based on tier: 2, 50, 200, or Infinity
  currentCustomerCount: number;           // Tracked for tier enforcement
  stripeCustomerId?: string;              // Platform's Stripe customer ID for this company
  stripeSubscriptionId?: string;          // Subscription for platform fee

  // Settings
  serviceAreas: string[];                 // ["Ohio", "Indiana"]
  monthlyRentalRate: number;              // Default rental rate (e.g., 50.00)
  supportedCategories?: ApplianceCategory[]; // Categories enabled for this business

  // Payment Processing Fees (platform takes from customer payments)
  cardFeePercent: number;                 // 3% convenience fee on card
  achFeeAmount: number;                   // 0 - ACH is free
  nsfFeeAmount: number;                   // $35 NSF fee

  // Payout Info
  payoutBankAccount?: string;             // Last 4 digits for display
  payoutRoutingNumber?: string;           // Masked
  payoutSchedule: 'weekly' | 'biweekly' | 'monthly';

  createdAt: string;
  updatedAt: string;
}

export const SUBSCRIPTION_LIMITS: Record<SubscriptionTier, { customers: number; price: number }> = {
  free: { customers: 2, price: 0 },
  starter: { customers: 50, price: 9.90 },
  professional: { customers: 200, price: 49 },
  unlimited: { customers: Infinity, price: 149 }
};

export type ApplianceCategory =
  | 'WASHER'
  | 'DRYER'
  | 'STACKED_UNIT'
  | 'REFRIGERATOR'
  | 'DISHWASHER'
  | 'RANGE_OVEN'
  | 'MICROWAVE'
  | 'HVAC_AC'
  | 'WATER_HEATER'
  | 'DEHUMIDIFIER'
  | 'COMMERCIAL_LAUNDRY'
  | 'LAWN_POWER'
  | 'OTHER';

export interface InventoryItem {
  id: string;
  serialNumber: string;
  type: ApplianceCategory;
  make: string;
  model: string;
  status: 'IN_STOCK' | 'RENTED' | 'MAINTENANCE' | 'RETIRED';
  condition: 'NEW' | 'LIKE_NEW' | 'GOOD' | 'FAIR' | 'POOR';
  purchaseDate: string;
  purchasePrice: number;
  monthlyRentalRate?: number;
  powerRequirements?: string; // e.g. "120V 15A" or "Gas"
  dimensions?: string;
  currentLocation: string; // "Warehouse" or Customer Address
  assignedToCustomerId?: string; // Link to customer
  notes?: string;
  createdAt: string;
  updatedAt: string;
  // Reservation tracking (v3.0)
  reservedForEventId?: string | null;
  reservedForCustomerId?: string | null;
  reservedAt?: string | null;
  rentedAt?: string;
}

export interface Application {
  id: string;
  userId: string; // Required for Firestore security rules
  companyId: string; // Required for multi-company isolation
  applicantName: string;
  email: string;
  phone: string;
  address: string;
  status: AppStatus;
  customerType?: 'APPLIANCE';  // All customers are appliance renters
  housingStatus?: string;
  applianceRequested?: string;

  // Admin Notes
  adminNotes?: string;
  rejectionReason?: string;

  // Photos
  waterHookup?: string; // Base64
  powerHookup?: string; // Base64
  ventHookup?: string; // Base64
  residenceProof?: string; // Base64

  submissionDate: string;
  installationDate?: string;
  leaseContent?: string;
  leaseSignature?: string;
  leaseSignedDate?: string;

  // Apartment Lease (Residence Tracking)
  apartmentLeaseStartDate?: string;
  apartmentLeaseEndDate?: string;

  // Risk & Background Check
  riskScore?: number; // 0-100
  riskAnalysis?: string;
  backgroundCheck?: {
    status: 'PENDING' | 'CLEAR' | 'FLAGGED';
    criminalRecordFound: boolean;
    paymentDefaults: boolean;
    details: string;
    checkedAt: string;
  };

  paymentDetails?: {
    last4: string;
    brand: string;
  };
  installationChecklist?: {
    waterConnected: boolean;
    drainSecured: boolean;
    powerStable: boolean;
    levelingFeetAdjusted: boolean;
    testCycleRun: boolean;
    customerConfirmed: boolean;
  };
  installPhoto?: string;

  // Presale tracking
  presaleSource?: 'waitlist' | 'early_access' | 'subscription_advance';
  presaleOrderId?: string;
  presaleCredits?: {
    depositAmount?: number;      // $99 for early access
    discountAmount?: number;      // $70 for early access
    totalCredit?: number;         // $169 for early access
    prepaidMonths?: number;       // 12 for subscription advance
    prepaidAmount?: number;       // $499 for subscription advance
  };
  creditsApplied?: boolean;
  applicationLinkSentAt?: string;
  paymentLinkSentAt?: string;
  // Admin counter-signing
  adminSignedAt?: string;
  adminAcknowledgedAt?: string;
  adminSignature?: string;
  // Payment tracking (Stripe webhook data)
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  paymentStatus?: 'PAID' | 'PAID_LATE_PENDING' | 'UNPAID';
  lastPaymentDate?: string;
  lastPaymentFailureDate?: string;
  // Termination & Legal tracking
  equipmentSerialNumber?: string;
  pickupScheduledDate?: string;
  equipmentReturned?: boolean;
  terminatedAt?: string;
  terminationReason?: string;
  theftReportedAt?: string;
  theftReportNumber?: string;
  purchaseInvoicedAt?: string;
  purchaseInvoiceAmount?: number;
  // Customer ID Information
  idPhoto?: string;           // Base64 or URL of ID image
  idType?: 'drivers_license' | 'state_id' | 'passport' | 'other';
  idExpirationDate?: string;  // ISO date string
  // Lease Versioning
  pendingLeaseVersion?: string;  // If set, customer must accept before dashboard access
  currentLeaseVersion?: string;  // Version they last accepted

  // Manual Lease Upload (for offline signatures)
  signedLeaseUpload?: string;    // URL of admin-uploaded signed lease document
  signedVia?: 'portal' | 'manual_upload';  // How the lease was signed


}

// Lease Template Versioning
export interface LeaseTemplate {
  id: string;
  version: string;         // "v1.0", "v1.1", etc.
  content: string;         // HTML lease text with placeholders
  createdAt: string;
  createdBy: string;       // Admin email
  isActive: boolean;       // Current active version
  pushedAt?: string;       // When pushed to customers
  pushedBy?: string;       // Admin who pushed
  notes?: string;          // Version notes
}

// Audit trail for lease acceptance (stored in applications/{id}/leaseAcceptances)
export interface LeaseAcceptance {
  id: string;
  leaseTemplateId: string;
  leaseVersion: string;
  acceptedAt: string;
  userId: string;
  userEmail: string;
  ipAddress: string;
  userAgent: string;
}

export interface Ticket {
  id: string;
  customerId: string;
  customerName: string;
  issue: string;
  status: 'OPEN' | 'RESOLVED' | 'IN_PROGRESS';
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  date: string;
  messages?: { sender: 'ADMIN' | 'CUSTOMER', text: string, date: string }[];
}

export interface FinancialMetric {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
  purchases: number; // New field for inventory purchases
}

export interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'INCOME' | 'EXPENSE';
  category: 'RENTAL' | 'INVENTORY' | 'MAINTENANCE' | 'SALARY' | 'UTILITIES' | 'MARKETING' | 'SERVICE' | 'OTHER';
}

// CRM Types
export interface Broadcast {
  id: string;
  subject: string;
  body: string;
  sentAt: string;
  sentBy: string;
  recipientCount: number;
}

export interface CustomerReport {
  id: string;
  customerId: string;
  customerName: string;
  type: 'CONDITION_REPORT' | 'MAINTENANCE_REQUEST';
  status: 'SUBMITTED' | 'REVIEWED' | 'ACTION_REQUIRED';

  // Condition Report Specifics
  photos?: {
    washerFront?: string;
    dryerFront?: string;
    ventConnection?: string;
    serialTag?: string;
  };
  conditionRating?: 'GOOD' | 'FAIR' | 'POOR';

  submittedAt: string;
  notes?: string;
}

export interface SystemMessage {
  id: string;
  customerId: string;
  type: 'BIRTHDAY' | 'LEASE_EXPIRY' | 'MAINTENANCE_REMINDER' | 'CLEANUP_REMINDER' | 'BROADCAST';
  subject: string;
  body: string;
  sentAt: string;
  status: 'SENT' | 'FAILED';
}

// Extended Application with CRM fields
// Maintenance Scheduler Types
export interface MaintenanceEvent {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  type: 'INSTALLATION' | 'PICKUP' | 'REPAIR' | 'INSPECTION' | 'REPORT_CHECK';
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  date: string; // YYYY-MM-DD
  timeSlot: string;
  technician?: string;
  notes?: string;
  createdAt?: string;
  completedAt?: string;
}

export interface ApplicationCRM extends Application {
  dob?: string; // Date of Birth for birthday messages
  leaseStartDate?: string;
  leaseEndDate?: string;
  lastMaintenanceReminder?: string;
  lastCleanupReminder?: string;
  lastReportDate?: string;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
}

// Email Tracking for legal evidence
export interface EmailTracking {
  id: string;
  customerId: string;
  customerEmail: string;
  type: 'GENERAL' | 'NOTICE_DEFAULT' | 'TERMINATION_DEMAND' | 'PICKUP_DEMAND' | 'THEFT_WARNING';
  subject: string;
  body: string;
  sentAt: string;
  status: 'SENT' | 'DELIVERED' | 'OPENED' | 'BOUNCED';
  deliveredAt?: string;
  openedAt?: string;
  trackingId: string;  // Unique ID for tracking pixel
  replyReceivedAt?: string;
  replyContent?: string;
}

// Activity Log for customer timeline
export interface ActivityLogEntry {
  id: string;
  customerId: string;
  type: 'EMAIL_SENT' | 'EMAIL_OPENED' | 'CUSTOMER_REPLY' | 'STATUS_CHANGE' | 'INVOICE_CREATED' | 'PAYMENT_RECEIVED' | 'PAYMENT_FAILED' | 'PICKUP_SCHEDULED' | 'EQUIPMENT_RETURNED' | 'THEFT_REPORTED' | 'PROFILE_UPDATE' | 'DOCUMENT_UPLOADED' | 'ID_EXPIRED' | 'ID_UPLOADED';
  timestamp: string;
  actor?: string;  // Who performed the action (admin email, system, customer)
  details: {
    subject?: string;
    body?: string;
    newStatus?: string;
    oldStatus?: string;
    amount?: number;
    emailTrackingId?: string;
    notes?: string;
    invoiceId?: string;      // Stripe invoice ID
    dueDate?: string;        // Invoice due date
    subscriptionId?: string; // Stripe subscription ID
    // Profile update tracking
    changes?: Array<{ field: string; oldValue: string; newValue: string }>;
    reason?: string;         // Admin-provided reason for change
    // Document upload tracking
    documentType?: string;   // 'waterHookup' | 'powerHookup' etc.
    previousVersion?: string; // URL/ID of archived document
  };
}

// Document version history for audit trail
export interface DocumentVersion {
  id: string;
  customerId: string;
  documentType: 'waterHookup' | 'powerHookup' | 'ventHookup' | 'residenceProof' | 'installPhoto';
  url: string;              // Base64 or URL of the document
  uploadedAt: string;       // When this version was uploaded
  uploadedBy: string;       // Admin email who uploaded
  replacedAt?: string;      // When this version was replaced by a newer one
  notes?: string;           // Optional notes about the document
}
